import * as React from 'react';
import { useState, useMemo } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Collapse,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

interface OAuthClient {
    guid: string;
    name: string;
    client_id: string;
    env: string[] | null;
}

interface JwtKey {
    id: string;
    kid: string;
    application_guid: string;
    api_key: string;
    api_config_guid: string | null;
    algorithm: string;
    status: string;
    env: string | null;
    created: string;
    creator: string;
    retired_at: string | null;
    revoked_at: string | null;
    oauth_clients: OAuthClient[];
    application_name: string;
    last_seen: string | null;
}

interface JwtKeyUsageDetail {
    kid: string;
    last_seen: string | null;
    total: number;
    daily_counts: Array<{ date: string; count: number }>;
}

const statusColors: Record<string, 'success' | 'warning' | 'error'> = {
    active: 'success',
    retired: 'warning',
    revoked: 'error',
};

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('fi-FI');
};

const formatLastSeen = (lastSeen: string | null) => {
    if (!lastSeen) return null;
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
};

const UsageDetail = ({ kid }: { kid: string }) => {
    const dataProvider = useDataProvider();
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin/jwt-keys/usage', kid],
        queryFn: () => dataProvider.getAny(`admin/jwt-keys/${kid}/usage`),
        staleTime: 900000,
    });

    if (isLoading) return <CircularProgress size={16} />;
    if (error)
        return (
            <Typography variant="caption" color="error">
                Failed to load usage
            </Typography>
        );

    const usage: JwtKeyUsageDetail = data?.data;
    if (!usage) return null;

    const maxCount = Math.max(...usage.daily_counts.map(d => d.count), 1);

    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Usage ({usage.total} requests in last 30 days)
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'flex-end',
                    height: 40,
                }}
            >
                {usage.daily_counts.slice(-30).map((day, i) => (
                    <Tooltip
                        key={i}
                        title={`${new Date(day.date).toLocaleDateString('fi-FI')}: ${day.count}`}
                    >
                        <Box
                            sx={{
                                width: 6,
                                minHeight: 2,
                                height: `${Math.max((day.count / maxCount) * 100, 5)}%`,
                                bgcolor:
                                    day.count > 0 ? 'primary.main' : 'grey.300',
                                borderRadius: 0.5,
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
};

const KeyRow = ({
    keyData,
    selected,
    onSelect,
    onRevoke,
}: {
    keyData: JwtKey;
    selected: boolean;
    onSelect: (kid: string) => void;
    onRevoke: (kid: string) => void;
}) => {
    const [open, setOpen] = useState(false);
    const clients = keyData.oauth_clients || [];

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell padding="checkbox">
                    <Checkbox
                        checked={selected}
                        onChange={() => onSelect(keyData.kid)}
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? (
                            <KeyboardArrowUpIcon />
                        ) : (
                            <KeyboardArrowDownIcon />
                        )}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">
                        {keyData.application_name}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Tooltip title={keyData.kid}>
                        <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 180 }}
                        >
                            {keyData.kid}
                        </Typography>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <Tooltip title={keyData.api_key}>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 200 }}
                            >
                                {keyData.api_key}
                            </Typography>
                        </Tooltip>
                        {keyData.api_config_guid && (
                            <Tooltip title="API Config">
                                <IconButton
                                    size="small"
                                    component={Link}
                                    to={`/admin/api-configs/${keyData.api_config_guid}`}
                                >
                                    <SettingsOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </TableCell>
                <TableCell>
                    {keyData.env ? (
                        <Chip label={keyData.env} size="small" color="info" />
                    ) : (
                        <Chip label="all" size="small" variant="outlined" />
                    )}
                </TableCell>
                <TableCell>
                    <Chip
                        label={keyData.status}
                        color={statusColors[keyData.status] || 'default'}
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    <Chip
                        label={keyData.algorithm || '?'}
                        size="small"
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>{formatDate(keyData.created)}</TableCell>
                <TableCell>{formatDate(keyData.retired_at)}</TableCell>
                <TableCell>
                    {keyData.last_seen ? (
                        <Tooltip title={formatDate(keyData.last_seen)}>
                            <Chip
                                label={formatLastSeen(keyData.last_seen)}
                                size="small"
                                color={(() => {
                                    const diffDays = Math.floor(
                                        (Date.now() -
                                            new Date(
                                                keyData.last_seen
                                            ).getTime()) /
                                            (1000 * 60 * 60 * 24)
                                    );
                                    if (diffDays <= 1) return 'success';
                                    if (diffDays <= 7) return 'warning';
                                    return 'default';
                                })()}
                                variant="outlined"
                            />
                        </Tooltip>
                    ) : (
                        <Typography variant="caption" color="text.disabled">
                            —
                        </Typography>
                    )}
                </TableCell>
                <TableCell>
                    <Chip
                        label={`${clients.length} client${clients.length !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        onClick={
                            clients.length > 0
                                ? () => setOpen(!open)
                                : undefined
                        }
                    />
                </TableCell>
                <TableCell>
                    {keyData.status === 'retired' && (
                        <Tooltip title="Revoke key">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => onRevoke(keyData.kid)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={13}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 1 }}>
                            {clients.length > 0 && (
                                <>
                                    <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                        color="text.secondary"
                                    >
                                        OAuth Clients
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Client ID</TableCell>
                                                <TableCell>Env</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {clients.map(client => (
                                                <TableRow key={client.guid}>
                                                    <TableCell>
                                                        {client.name}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontFamily:
                                                                    'monospace',
                                                            }}
                                                        >
                                                            {client.client_id}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {client.env?.join(
                                                            ', '
                                                        ) || '—'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </>
                            )}
                            <UsageDetail kid={keyData.kid} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

interface JwtKeyListProps {
    embedded?: boolean;
}

const JwtKeyList = ({ embedded }: JwtKeyListProps) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [rotating, setRotating] = useState(false);
    const [envFilter, setEnvFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('active');
    const [appFilter, setAppFilter] = useState<string>('');
    const [clientFilter, setClientFilter] = useState<string>('');

    const [nocache, setNocache] = useState(false);
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin/jwt-keys', nocache],
        queryFn: () =>
            dataProvider.getAny(
                'admin/jwt-keys',
                `range=${encodeURIComponent('[0,999]')}${nocache ? '&nocache=1' : ''}`
            ),
        staleTime: 30000,
    });

    const allKeys: JwtKey[] = data?.data || [];

    const envOptions = useMemo(
        () =>
            Array.from(
                new Set(allKeys.map(k => k.env).filter(Boolean))
            ).sort() as string[],
        [allKeys]
    );
    const appOptions = useMemo(
        () =>
            Array.from(
                new Set(allKeys.map(k => k.application_name).filter(Boolean))
            ).sort() as string[],
        [allKeys]
    );

    const filteredKeys = useMemo(() => {
        return allKeys.filter(key => {
            if (envFilter && key.env !== envFilter) return false;
            if (statusFilter && key.status !== statusFilter) return false;
            if (appFilter && key.application_name !== appFilter) return false;
            if (clientFilter) {
                const term = clientFilter.toLowerCase();
                const hasMatch = key.oauth_clients?.some(c =>
                    c.name
                        .replace(/-(?:dev|next|staging|prod)$/, '')
                        .toLowerCase()
                        .includes(term)
                );
                if (!hasMatch) return false;
            }
            return true;
        });
    }, [allKeys, envFilter, statusFilter, appFilter, clientFilter]);

    const handleSelect = (kid: string) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (next.has(kid)) {
                next.delete(kid);
            } else {
                next.add(kid);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        const visibleKids = filteredKeys.map(k => k.kid);
        const allSelected = visibleKids.every(kid => selectedKeys.has(kid));

        setSelectedKeys(prev => {
            const next = new Set(prev);
            for (const kid of visibleKids) {
                if (allSelected) {
                    next.delete(kid);
                } else {
                    next.add(kid);
                }
            }
            return next;
        });
    };

    const handleRotate = async () => {
        if (selectedKeys.size === 0) {
            notify('Select keys to rotate', { type: 'warning' });
            return;
        }

        const keysToRotate: Array<{
            application_guid: string;
            api_key: string;
            algorithm?: string;
            env?: string | null;
        }> = [];
        for (const key of filteredKeys) {
            if (selectedKeys.has(key.kid)) {
                keysToRotate.push({
                    application_guid: key.application_guid,
                    api_key: key.api_key,
                    algorithm: key.algorithm,
                    env: key.env,
                });
            }
        }

        setRotating(true);
        try {
            await (dataProvider as any).rotateJwtKeys(keysToRotate);
            notify(`${keysToRotate.length} key(s) rotated successfully`, {
                type: 'success',
            });
            setSelectedKeys(new Set());
            refetch();
            refresh();
        } catch (err: any) {
            notify(err?.message || 'Rotation failed', { type: 'error' });
        } finally {
            setRotating(false);
        }
    };

    const handleRevoke = async (kid: string) => {
        try {
            await (dataProvider as any).revokeJwtKey(kid);
            notify('Key revoked successfully', { type: 'success' });
            refetch();
            refresh();
        } catch (err: any) {
            notify(err?.message || 'Revoke failed', { type: 'error' });
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Failed to load JWT keys</Alert>;
    }

    const visibleKids = filteredKeys.map(k => k.kid);
    const allSelected =
        visibleKids.length > 0 &&
        visibleKids.every(kid => selectedKeys.has(kid));
    const someSelected =
        !allSelected && visibleKids.some(kid => selectedKeys.has(kid));

    return (
        <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Aside filters */}
            <Paper
                sx={{
                    p: 2,
                    minWidth: 200,
                    maxWidth: 220,
                    flexShrink: 0,
                    alignSelf: 'flex-start',
                }}
                elevation={1}
            >
                <Typography variant="subtitle2" gutterBottom>
                    Filters
                </Typography>
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Env</InputLabel>
                    <Select
                        value={envFilter}
                        label="Env"
                        onChange={e => setEnvFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {envOptions.map(env => (
                            <MenuItem key={env} value={env}>
                                {env}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={e => setStatusFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="retired">Retired</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Application</InputLabel>
                    <Select
                        value={appFilter}
                        label="Application"
                        onChange={e => setAppFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {appOptions.map(app => (
                            <MenuItem key={app} value={app}>
                                {app}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    fullWidth
                    label="Client"
                    value={clientFilter}
                    onChange={e => setClientFilter(e.target.value)}
                    placeholder="e.g. cloubi, nova"
                />
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 2 }}
                >
                    {filteredKeys.length} key
                    {filteredKeys.length !== 1 ? 's' : ''}
                    {selectedKeys.size > 0
                        ? ` · ${selectedKeys.size} selected`
                        : ''}
                </Typography>
            </Paper>

            {/* Main content */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={() => {
                                setNocache(true);
                                refetch().then(() => setNocache(false));
                            }}
                            size="small"
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        startIcon={
                            rotating ? (
                                <CircularProgress size={16} />
                            ) : (
                                <RotateLeftIcon />
                            )
                        }
                        disabled={selectedKeys.size === 0 || rotating}
                        onClick={handleRotate}
                    >
                        Rotate Selected ({selectedKeys.size})
                    </Button>
                </Box>

                {filteredKeys.length === 0 && (
                    <Alert severity="info">
                        No JWT signing keys match the current filters
                    </Alert>
                )}

                {filteredKeys.length > 0 && (
                    <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={someSelected}
                                            onChange={handleSelectAll}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: 40 }} />
                                    <TableCell>Application</TableCell>
                                    <TableCell>Kid</TableCell>
                                    <TableCell>API Key</TableCell>
                                    <TableCell>Env</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Algorithm</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Retired</TableCell>
                                    <TableCell>Last Seen</TableCell>
                                    <TableCell>OAuth Clients</TableCell>
                                    <TableCell sx={{ width: 50 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredKeys.map(key => (
                                    <KeyRow
                                        key={key.kid}
                                        keyData={key}
                                        selected={selectedKeys.has(key.kid)}
                                        onSelect={handleSelect}
                                        onRevoke={handleRevoke}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};

export default JwtKeyList;
